import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { BOOKING_STAGES, FINISHED_STAGES, type BookingStage } from "@/lib/booking-stages";


const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date");

const createBookingSchema = z.object({
  carSlug: z.string().min(1).max(120),
  carName: z.string().min(1).max(160),
  customerName: z.string().trim().min(2).max(120),
  customerEmail: z.string().trim().email().max(255),
  customerPhone: z.string().trim().min(5).max(40),
  pickupDate: isoDate,
  returnDate: isoDate,
  pickupLocation: z.string().trim().min(2).max(200),
  pickupType: z.enum(["main", "otopeni", "custom"]).default("main"),
  customPickupAddress: z.string().trim().max(300).optional().or(z.literal("")),
  dropoffLocation: z.string().trim().min(2).max(200).optional(),
  dropoffType: z.enum(["main", "otopeni", "custom"]).default("main"),
  customDropoffAddress: z.string().trim().max(300).optional().or(z.literal("")),
  deliveryFee: z.number().nonnegative().max(1000).default(0),
  airportFee: z.number().nonnegative().max(1000).default(0),
  notes: z.string().trim().max(1000).optional().or(z.literal("")),
  otherRequests: z.string().trim().max(2000).optional().or(z.literal("")),
  pricePerDay: z.number().nonnegative().max(100000),
  totalEstimate: z.number().nonnegative().max(1000000),
});

export const createBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => createBookingSchema.parse(data))
  .handler(async ({ data, context }) => {
    const combinedNotes = [
      data.notes || "",
      data.otherRequests ? `Other requests: ${data.otherRequests}` : "",
    ]
      .filter(Boolean)
      .join("\n\n");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const {
      sendEmail,
      bookingReceivedEmail,
      adminNotificationEmail,
      ADMIN_NOTIFY_EMAIL,
    } = await import("./email.server");

    // Prices are never trusted from the client: recompute from the canonical
    // fleet/pricing tables and the requested date range.
    const { cars } = await import("@/data/cars");
    const { dailyRate } = await import("@/data/pricing");
    const { AIRPORT_FEE, DELIVERY_FEE } = await import("@/data/locations");

    const car = cars.find((c) => c.slug === data.carSlug);
    if (!car) throw new Error("Unknown car. Please pick a vehicle from our fleet.");

    const diff =
      (new Date(data.returnDate).getTime() - new Date(data.pickupDate).getTime()) / 86_400_000;
    const days = Number.isFinite(diff) && diff > 0 ? Math.ceil(diff) : 1;

    const ratePerDay = dailyRate(car.slug, days, car.pricePerDay);
    const customSides =
      (data.pickupType === "custom" ? 1 : 0) + (data.dropoffType === "custom" ? 1 : 0);
    const airportSides =
      (data.pickupType === "otopeni" ? 1 : 0) + (data.dropoffType === "otopeni" ? 1 : 0);
    const deliveryFee = customSides * DELIVERY_FEE;
    const airportFee = airportSides * AIRPORT_FEE;
    const totalEstimate = days * ratePerDay + deliveryFee + airportFee;

    const { data: booking, error } = await supabaseAdmin
      .from("bookings")
      .insert({
        car_slug: car.slug,
        car_name: car.name,
        customer_name: data.customerName,
        customer_email: data.customerEmail,
        customer_phone: data.customerPhone,
        customer_user_id: context.userId,
        stage: "new",
        pickup_date: data.pickupDate,
        return_date: data.returnDate,
        pickup_location: data.pickupLocation,
        pickup_type: data.pickupType,
        custom_pickup_address: data.customPickupAddress || null,
        dropoff_location: data.dropoffLocation || data.pickupLocation,
        dropoff_type: data.dropoffType,
        custom_dropoff_address: data.customDropoffAddress || null,
        delivery_fee: deliveryFee,
        airport_fee: airportFee,
        notes: combinedNotes || null,
        price_per_day: ratePerDay,
        total_estimate: totalEstimate,
      })

      .select("id")
      .single();


    if (error || !booking) {
      console.error("[bookings] insert failed", error);
      throw new Error("We could not save your request. Please try again or call us.");
    }

    await supabaseAdmin.from("booking_status_history").insert({
      booking_id: booking.id,
      stage: "new",
      source: "customer",
      note: "Booking request submitted",
      changed_by: context.userId,
    });


    const payload = {
      id: booking.id,
      carName: car.name,
      customerName: data.customerName,
      customerEmail: data.customerEmail,
      customerPhone: data.customerPhone,
      pickupDate: data.pickupDate,
      returnDate: data.returnDate,
      pickupLocation: data.pickupLocation,
      dropoffLocation: data.dropoffLocation || data.pickupLocation,
      pickupType: data.pickupType,
      dropoffType: data.dropoffType,
      deliveryFee,
      airportFee,
      notes: combinedNotes || null,
      totalEstimate,

    };

    const customerMail = bookingReceivedEmail(payload);
    const adminMail = adminNotificationEmail(payload);
    await Promise.allSettled([
      sendEmail(data.customerEmail, customerMail.subject, customerMail.html, {
        bookingId: booking.id,
        threadEmail: data.customerEmail,
      }),
      sendEmail(ADMIN_NOTIFY_EMAIL, adminMail.subject, adminMail.html, {
        bookingId: booking.id,
        threadEmail: data.customerEmail,
      }),
    ]);

    return { id: booking.id as string };
  });

export const listBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("bookings")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

const updateSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "confirmed", "cancelled", "completed"]).optional(),
  stage: z.enum(BOOKING_STAGES).optional(),
  reconfirmationStatus: z.enum(["not_sent", "sent", "confirmed", "no_response"]).optional(),
  adminNotes: z.string().trim().max(2000).optional(),
  note: z.string().trim().max(500).optional(),
});

/** Stage changes that trigger a customer email. */
async function sendStageEmail(stage: BookingStage, booking: Record<string, unknown>) {
  const mod = await import("./email.server");
  const payload = toEmailData(booking);
  const build = {
    confirmed: mod.bookingConfirmedEmail,
    pre_pickup: mod.prePickupEmail,
    ready_for_pickup: mod.readyForPickupEmail,
    return_pending: mod.returnReminderEmail,
    completed: mod.reviewRequestEmail,
    review_pending: mod.reviewRequestEmail,
  }[stage as string];
  if (!build) return false;
  const mail = build(payload);
  const result = await mod.sendEmail(payload.customerEmail, mail.subject, mail.html, {
    bookingId: payload.id,
    threadEmail: payload.customerEmail,
  });
  return result.sent;
}

export const updateBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => updateSchema.parse(data))
  .handler(async ({ data, context }) => {
    const patch: {
      status?: "pending" | "confirmed" | "cancelled" | "completed";
      stage?: BookingStage;
      reconfirmation_status?: "not_sent" | "sent" | "confirmed" | "no_response";
      admin_notes?: string | null;
    } = {};
    if (data.status) patch.status = data.status;
    if (data.stage) patch.stage = data.stage;
    if (data.reconfirmationStatus) patch.reconfirmation_status = data.reconfirmationStatus;
    if (data.adminNotes !== undefined) patch.admin_notes = data.adminNotes || null;

    const { data: booking, error } = await context.supabase
      .from("bookings")
      .update(patch)
      .eq("id", data.id)
      .select("*")
      .single();

    if (error || !booking) throw new Error(error?.message ?? "Booking not found");

    if (data.stage) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { error: historyError } = await supabaseAdmin.from("booking_status_history").insert({
        booking_id: booking.id,
        stage: data.stage,
        source: "admin",
        note: data.note || null,
        changed_by: context.userId,
      });
      if (historyError) console.error("[bookings] history insert failed", historyError);
      await sendStageEmail(data.stage, booking as Record<string, unknown>).catch((err) =>
        console.error("[bookings] stage email failed", err),
      );
      if (data.stage === "completed") {
        await context.supabase
          .from("bookings")
          .update({ stage: "review_pending" })
          .eq("id", booking.id);
        const { error: reviewHistoryError } = await supabaseAdmin
          .from("booking_status_history")
          .insert({
          booking_id: booking.id,
          stage: "review_pending",
          source: "system",
          note: "Review request sent to customer",
        });
        if (reviewHistoryError)
          console.error("[bookings] history insert failed", reviewHistoryError);
        return { ...booking, stage: "review_pending" as const };
      }
    } else if (data.status === "confirmed") {
      await sendStageEmail("confirmed", booking as Record<string, unknown>).catch((err) =>
        console.error("[bookings] confirmation email failed", err),
      );
    }

    return booking;
  });

export const listBookingHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await context.supabase
      .from("booking_status_history")
      .select("id, stage, note, source, created_at")
      .eq("booking_id", data.id)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

/** Customer-facing: all bookings owned by the signed-in account. */
export const listMyBookings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("bookings")
      .select("*")
      .eq("customer_user_id", context.userId)
      .order("pickup_date", { ascending: false });
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const getMyBooking = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: booking, error } = await context.supabase
      .from("bookings")
      .select("*")
      .eq("id", data.id)
      .eq("customer_user_id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!booking) return null;
    const { data: history } = await context.supabase
      .from("booking_status_history")
      .select("id, stage, note, created_at")
      .eq("booking_id", data.id)
      .order("created_at", { ascending: false });
    return { booking, history: history ?? [] };
  });

/** Customer-facing request from the booking portal (extend, support, issue, accident). */
export const sendBookingRequest = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        kind: z.enum(["Extend rental", "Contact support", "Report an issue", "Accident assistance"]),
        message: z.string().trim().min(5).max(2000),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: booking, error } = await context.supabase
      .from("bookings")
      .select("*")
      .eq("id", data.id)
      .eq("customer_user_id", context.userId)
      .maybeSingle();
    if (error || !booking) throw new Error("Booking not found");

    const mod = await import("./email.server");
    const payload = toEmailData(booking as Record<string, unknown>);
    const office = mod.customerRequestEmail(payload, data.kind, data.message);
    const ack = mod.customerRequestAckEmail(payload, data.kind);
    await Promise.allSettled([
      mod.sendEmail(mod.ADMIN_NOTIFY_EMAIL, office.subject, office.html, {
        bookingId: payload.id,
        threadEmail: payload.customerEmail,
      }),
      mod.sendEmail(payload.customerEmail, ack.subject, ack.html, {
        bookingId: payload.id,
        threadEmail: payload.customerEmail,
      }),
    ]);
    return { sent: true };
  });

/** Admin sales report over finished rentals. */
export const salesReport = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { data, error } = await context.supabase
      .from("bookings")
      .select("id, car_name, car_slug, customer_name, pickup_date, return_date, total_estimate, stage")
      .in("stage", FINISHED_STAGES)
      .order("return_date", { ascending: false });
    if (error) throw new Error(error.message);

    const rows = data ?? [];
    const revenue = rows.reduce((sum, r) => sum + Number(r.total_estimate ?? 0), 0);
    const days = rows.reduce((sum, r) => {
      const d =
        (new Date(r.return_date).getTime() - new Date(r.pickup_date).getTime()) / 86_400_000;
      return sum + Math.max(1, Math.round(d));
    }, 0);

    const byMonth = new Map<string, { month: string; count: number; revenue: number }>();
    const byCar = new Map<string, { car: string; count: number; revenue: number }>();
    for (const r of rows) {
      const month = r.return_date.slice(0, 7);
      const m = byMonth.get(month) ?? { month, count: 0, revenue: 0 };
      m.count += 1;
      m.revenue += Number(r.total_estimate ?? 0);
      byMonth.set(month, m);

      const c = byCar.get(r.car_slug) ?? { car: r.car_name, count: 0, revenue: 0 };
      c.count += 1;
      c.revenue += Number(r.total_estimate ?? 0);
      byCar.set(r.car_slug, c);
    }

    return {
      totals: {
        bookings: rows.length,
        revenue,
        rentalDays: days,
        averageBooking: rows.length ? revenue / rows.length : 0,
      },
      byMonth: [...byMonth.values()].sort((a, b) => b.month.localeCompare(a.month)),
      byCar: [...byCar.values()].sort((a, b) => b.revenue - a.revenue),
      rows,
    };
  });


function toEmailData(booking: Record<string, unknown>) {
  return {
    id: String(booking["id"]),
    carName: String(booking["car_name"]),
    customerName: String(booking["customer_name"]),
    customerEmail: String(booking["customer_email"]),
    customerPhone: String(booking["customer_phone"]),
    pickupDate: String(booking["pickup_date"]),
    returnDate: String(booking["return_date"]),
    pickupLocation: String(booking["pickup_location"]),
    dropoffLocation: String(booking["dropoff_location"] ?? booking["pickup_location"]),
    pickupType: (booking["pickup_type"] as string | null) ?? null,
    dropoffType: (booking["dropoff_type"] as string | null) ?? null,
    deliveryFee: booking["delivery_fee"] ? Number(booking["delivery_fee"]) : 0,
    airportFee: booking["airport_fee"] ? Number(booking["airport_fee"]) : 0,
    notes: (booking["notes"] as string | null) ?? null,
    totalEstimate: booking["total_estimate"] ? Number(booking["total_estimate"]) : null,
  };
}

export const sendReconfirmation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) => z.object({ id: z.string().uuid() }).parse(data))
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const token = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");

    const { data: booking, error } = await context.supabase
      .from("bookings")
      .update({
        reconfirmation_token: token,
        reconfirmation_sent_at: new Date().toISOString(),
        reconfirmation_status: "sent",
      })
      .eq("id", data.id)
      .select("*")
      .single();

    if (error || !booking) throw new Error(error?.message ?? "Booking not found");

    const { sendEmail, reconfirmationRequestEmail, SITE_URL } = await import("./email.server");
    const mail = reconfirmationRequestEmail(
      toEmailData(booking as Record<string, unknown>),
      `${SITE_URL}/reconfirm/${token}`,
    );
    const result = await sendEmail(booking.customer_email, mail.subject, mail.html, {
      bookingId: booking.id,
      threadEmail: booking.customer_email,
    });
    return { sent: result.sent, booking };
  });

export const confirmBookingByToken = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().trim().min(20).max(120) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: booking } = await supabaseAdmin
      .from("bookings")
      .select("*")
      .eq("reconfirmation_token", data.token)
      .maybeSingle();

    if (!booking) return { status: "invalid" as const };

    if (booking.reconfirmation_status === "confirmed") {
      return {
        status: "already" as const,
        customerName: booking.customer_name,
        carName: booking.car_name,
        pickupDate: booking.pickup_date,
      };
    }

    const { data: updated, error } = await supabaseAdmin
      .from("bookings")
      .update({ reconfirmation_status: "confirmed", reconfirmed_at: new Date().toISOString() })
      .eq("id", booking.id)
      .select("*")
      .single();

    if (error || !updated) throw new Error("Could not confirm this booking. Please call us.");

    const { sendEmail, reconfirmationThankYouEmail } = await import("./email.server");
    const mail = reconfirmationThankYouEmail(toEmailData(updated as Record<string, unknown>));
    await sendEmail(updated.customer_email, mail.subject, mail.html, {
      bookingId: updated.id,
      threadEmail: updated.customer_email,
    });

    return {
      status: "confirmed" as const,
      customerName: updated.customer_name,
      carName: updated.car_name,
      pickupDate: updated.pickup_date,
    };
  });

export const subscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ email: z.string().trim().email().max(255) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase();

    const { data: existing } = await supabaseAdmin
      .from("newsletter_subscribers")
      .select("id")
      .eq("email", email)
      .maybeSingle();

    if (existing) return { status: "already_subscribed" as const };

    const { error } = await supabaseAdmin.from("newsletter_subscribers").insert({ email });
    if (error) {
      if (error.code === "23505") return { status: "already_subscribed" as const };
      console.error("[newsletter] insert failed", error);
      throw new Error("Could not subscribe right now. Please try again.");
    }

    const { sendEmail, newsletterWelcomeEmail } = await import("./email.server");
    const mail = newsletterWelcomeEmail();
    await sendEmail(email, mail.subject, mail.html, { log: true }).catch((err) =>
      console.error("[newsletter] welcome email failed", err),
    );

    return { status: "subscribed" as const };
  });

export const unsubscribeNewsletter = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ email: z.string().trim().email().max(255) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("newsletter_subscribers")
      .delete()
      .eq("email", data.email.toLowerCase());
    if (error) {
      console.error("[newsletter] unsubscribe failed", error);
      throw new Error("Could not unsubscribe right now. Please try again.");
    }
    return { status: "unsubscribed" as const };
  });

export const isCurrentUserAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (error) throw new Error(error.message);
    return { isAdmin: Boolean(data) };
  });

/** Admin: decline a reservation, apologise and issue a 10% goodwill voucher. */
export const declineBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        reason: z.string().trim().max(500).optional().or(z.literal("")),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: booking, error } = await supabaseAdmin
      .from("bookings")
      .update({ stage: "cancelled", status: "cancelled" })
      .eq("id", data.id)
      .select("*")
      .single();
    if (error || !booking) throw new Error(error?.message ?? "Booking not found");

    const code = `CRB10-${crypto.randomUUID().replace(/-/g, "").slice(0, 6).toUpperCase()}`;
    const expires = new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10);

    const { error: voucherError } = await supabaseAdmin.from("booking_vouchers").insert({
      code,
      discount_percent: 10,
      booking_id: booking.id,
      customer_email: booking.customer_email,
      customer_name: booking.customer_name,
      reason: data.reason || null,
      expires_at: expires,
    });
    if (voucherError) console.error("[bookings] voucher insert failed", voucherError);

    await supabaseAdmin.from("booking_status_history").insert({
      booking_id: booking.id,
      stage: "cancelled",
      source: "admin",
      note: `Declined — 10% voucher ${code} issued${data.reason ? `: ${data.reason}` : ""}`,
      changed_by: context.userId,
    });

    const { sendEmail, bookingDeclinedEmail } = await import("./email.server");
    const mail = bookingDeclinedEmail(
      toEmailData(booking as Record<string, unknown>),
      { code, percent: 10, expires },
      data.reason || undefined,
    );
    const result = await sendEmail(booking.customer_email, mail.subject, mail.html, {
      bookingId: booking.id,
      threadEmail: booking.customer_email,
    });

    return { sent: result.sent, code, booking };
  });
